/**
 * Local Storage Utility for Locuno Project
 * Provides type-safe local storage operations with error handling
 */

// Storage Keys Constants
export const STORAGE_KEYS = {
  // Authentication
  AUTH_TOKEN: "locuno_auth_token",
  USER_DATA: "locuno_user_data",
  IS_AUTHENTICATED: "locuno_is_authenticated",

  // User Preferences
  USER_PREFERENCES: "locuno_user_preferences",
  THEME_SETTINGS: "locuno_theme_settings",
  LANGUAGE: "locuno_language",

  // Shopping & Marketplace
  SHOPPING_CART: "locuno_shopping_cart",
  WISHLIST: "locuno_wishlist",
  RECENT_PRODUCTS: "locuno_recent_products",

  // Posts & Content
  DRAFT_POST: "locuno_draft_post",
  SAVED_POSTS: "locuno_saved_posts",

  // Activities & History
  SEARCH_HISTORY: "locuno_search_history",
  RECENT_PLACES: "locuno_recent_places",
  BROWSING_HISTORY: "locuno_browsing_history",

  // App State
  LAST_VISITED_PAGE: "locuno_last_visited_page",
  APP_VERSION: "locuno_app_version",
} as const;

// Type Definitions
export interface UserData {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  bio?: string;
  gender?: string;
  location?: string;
  badge?: string;
  socialLinks?: {
    facebook?: string;
    website?: string;
  };
}

export interface UserPreferences extends Record<string, unknown> {
  notifications: boolean;
  darkMode: boolean;
  language: "vi" | "en";
  autoSave: boolean;
  showOnlineStatus: boolean;
}

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  sellerId: string;
}

export interface DraftPost {
  id: string;
  title: string;
  content: string;
  type: "cho" | "nhờ";
  location?: string;
  images?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface SearchHistoryItem {
  query: string;
  timestamp: string;
  category?: string;
}

// Generic Local Storage Functions
class LocalStorageManager {
  /**
   * Safely get item from localStorage with error handling
   */
  static getItem<T>(key: string, defaultValue: T): T {
    try {
      if (typeof window === "undefined") return defaultValue;

      const item = localStorage.getItem(key);
      if (item === null) return defaultValue;

      return JSON.parse(item);
    } catch (error) {
      console.warn(`Error reading from localStorage key "${key}":`, error);
      return defaultValue;
    }
  }

  /**
   * Safely set item to localStorage with error handling
   */
  static setItem<T>(key: string, value: T): boolean {
    try {
      if (typeof window === "undefined") return false;

      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.warn(`Error writing to localStorage key "${key}":`, error);
      return false;
    }
  }

  /**
   * Remove item from localStorage
   */
  static removeItem(key: string): boolean {
    try {
      if (typeof window === "undefined") return false;

      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn(`Error removing from localStorage key "${key}":`, error);
      return false;
    }
  }

  /**
   * Clear all localStorage data
   */
  static clear(): boolean {
    try {
      if (typeof window === "undefined") return false;

      localStorage.clear();
      return true;
    } catch (error) {
      console.warn("Error clearing localStorage:", error);
      return false;
    }
  }

  /**
   * Check if localStorage is available
   */
  static isAvailable(): boolean {
    try {
      if (typeof window === "undefined") return false;

      const test = "__localStorage_test__";
      localStorage.setItem(test, "test");
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }
}

export default LocalStorageManager;
